import React, { useCallback, useMemo, useState } from 'react';
import {
  ITab,
  OutboundCallModal,
  OutboundCallModalProvider,
  TabNameEnum,
  OutboundCallModalMode,
} from '@app/layouts/MainLayout/components/OutboundCall';
import { TransferringCallConfirmation } from './TransferringCallConfirmation';
import { TransferringCallProvider } from './context';
import { inboxLocators } from '@locators/inbox';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import usePermissions from '@app/permissions/usePermissions';
import { permissions } from '@app/permissions/permissions';

type TransferringCallProps = {
  task: any;
  onClose: () => void;
};

export const TransferringCall: React.FC<TransferringCallProps> = React.memo(
  ({ task, onClose }) => {
    const [activeStep, setActiveStep] = useState(0);
    const {
      [FeatureFlagKeys.featureInboxCallTransferToAgent]:
        featureInboxCallTransferToAgent,
      [FeatureFlagKeys.featureInboxCallTransferToQueue]:
        featureInboxCallTransferToQueue,
      [FeatureFlagKeys.featureCallTransferToTechnicians]:
        featureCallTransferToTechnicians,
    } = useFeatureFlags();

    const hasTransferToTechniciansPermission = usePermissions(
      [
        permissions.general.ACTION_CALL_OPTIONS.CALL_TRANSFER_TO_TECHNICIANS
          .VIEW,
      ],
      'all',
    );

    const showTechniciansTab =
      featureCallTransferToTechnicians && hasTransferToTechniciansPermission;

    const callModalTabs = useMemo<ITab[]>(
      () => [
        {
          value: TabNameEnum.Employees,
          disabled: !featureInboxCallTransferToAgent,
          selected: !!featureInboxCallTransferToAgent,
        },
        ...(showTechniciansTab ? [{ value: TabNameEnum.Technicians }] : []),
        ...(featureInboxCallTransferToQueue
          ? [{ value: TabNameEnum.Queue }]
          : []),
        {
          value: TabNameEnum.Keypad,
          selected: !featureInboxCallTransferToAgent,
        },
      ],
      [
        featureInboxCallTransferToAgent,
        featureInboxCallTransferToQueue,
        showTechniciansTab,
      ],
    );

    const mode: OutboundCallModalMode = featureInboxCallTransferToQueue
      ? 'transfer'
      : 'outbound';

    const handleNext = useCallback(() => {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }, []);

    const handleBack = useCallback(() => {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }, []);

    const cancelTransfer = useCallback(() => {
      onClose?.();
    }, [onClose]);

    return (
      <OutboundCallModalProvider>
        <TransferringCallProvider task={task} onClose={onClose}>
          <div>
            <OutboundCallModal
              open={activeStep === 0}
              setOpen={cancelTransfer}
              title={'Transfer a Call'}
              id={inboxLocators.callBar.transferModal}
              tabs={callModalTabs}
              actionButton={{
                text: 'Next',
                onClick: handleNext,
              }}
              onClose={cancelTransfer}
              animate={false}
              mode={mode}
            />
            {activeStep === 1 && (
              <TransferringCallConfirmation handleBack={handleBack} />
            )}
          </div>
        </TransferringCallProvider>
      </OutboundCallModalProvider>
    );
  },
);
